import {Util} from 'yougile-util'
import {DataObjectId} from './data-object'
import {JSONLike} from './diff'

export interface ChecklistItem {
  id: DataObjectId
  title: string
  isCompleted: boolean
}

export interface Checklist {
  id: DataObjectId
  title: string
  items: ChecklistItem[]
}

export const getChecklists = (hub: {data?: JSONLike}): Checklist[] => {
  if (!hub || !hub.data || !hub.data.checklists) {
    return []
  }
  return hub.data.checklists
}

export const getChecklistsAsText = (checklists: Checklist[] | undefined) => {
  if (!checklists || checklists.length === 0) {
    return ''
  }
  /**
   * The format is taken from macos Notes.
   * To show completed subtasks another good options are ✔ and ✓ symbols
   */
  return checklists
    .map(list =>
      list
        ? list.title +
          (list.items
            ?.map(i =>
              i ? ' - [' + (i.isCompleted ? 'x' : ' ') + '] ' + i.title : ''
            )
            ?.join('\n') ?? '')
        : ''
    )
    .join('\n\n')
}

export const differsOnlyInCompleteness = (
  list1: Checklist[],
  list2: Checklist[]
): boolean => {
  if (!list1 || !list2 || list1.length !== list2.length) {
    return false
  }
  for (let ix = 0; ix < list1.length; ix++) {
    const ch1 = list1[ix]
    const ch2 = list2[ix]
    if (
      !ch1 ||
      !ch2 ||
      ch1.id !== ch2.id ||
      ch1.title !== ch2.title ||
      (ch1.items || []).length !== (ch2.items || []).length
    ) {
      return false
    }
    for (let i = 0; i < (ch1.items || []).length; i++) {
      const it1 = ch1.items[i]
      const it2 = ch2.items[i]
      if (!it1 || !it2 || it1.id !== it2.id || it1.title !== it2.title) {
        return false
      }
    }
  }
  return true
}

const fixSubtaskLists = (lists: Checklist[] = []): Checklist[] => {
  return lists.filter(Util.identity).map(fixSubtaskList)
}

export const cleanChecklists = (lists: Checklist[]): Checklist[] => {
  const all = fixSubtaskLists(lists)
  all.forEach(list => {
    list.items = Util.removeDuplicateIds(list.items.filter(s => s?.title))
  })
  return all
}

const fixSubtaskList = (list: Checklist): Checklist => {
  return {
    id: list?.id || (Util.UUID() as DataObjectId),
    title: list?.title || '',
    items: list?.items?.map ? list.items.map(fixSubtask) : []
  }
}

const fixSubtask = (item: ChecklistItem): ChecklistItem => {
  return {
    id: item?.id || (Util.UUID() as DataObjectId),
    isCompleted: item && !!item.isCompleted,
    title: item?.title || ''
  }
}
